import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { NotFoundError } from 'src/shared/error';

import type { RootPageDataParams, RootPageDataReturn } from '../types';

import { getPetitionMetadata } from './utilities/getPetitionMetadata';
import { getPetitionPromotionStats } from './utilities/getPetitionPromotionStats';
import { getPetitionReadyToSharePosts } from './utilities/getPetitionReadyToSharePosts';
import { getPetitionRecentSigners } from './utilities/getPetitionRecentSigners';
import { getPetitionTotalViews } from './utilities/getPetitionTotalViews';
import { getStarterContactDmConfig } from './utilities/getStarterContactDmConfig';
import { getStarterRootPageData } from './utilities/getStarterRootPageData';
import { shouldLoadShareRecommendations } from './utilities/shouldLoadShareRecommendations';

// eslint-disable-next-line max-lines-per-function
export async function getRootPageData(
	utilityContext: UtilityContext,
	{ slug, countryCode, locale }: RootPageDataParams,
): Promise<RootPageDataReturn> {
	const {
		gql: { fetch },
		i18n,
	} = utilityContext;

	// Get a timestamp that's 24 hours before current time to send to GET petition tasks query
	const completedSince = new Date(new Date().getTime() - 24 * 60 * 60 * 1000).toISOString();

	const [{ emailEnabled, emailSubjectLine, emailBody }, includeShareRecommendations, recentSignersConnection] =
		await Promise.all([
			getStarterContactDmConfig(utilityContext),
			shouldLoadShareRecommendations(utilityContext),
			getPetitionRecentSigners(utilityContext, { slug }),
		]);
	const { data } = await getStarterRootPageData(fetch, {
		slug,
		completedSince,
		locale,
		countryCode,
		emailEnabled,
	});

	if (!data.petition) {
		throw new NotFoundError();
	}

	const { totalViews, totalViewsLocalized } = getPetitionTotalViews(data.petition, i18n);
	const petition = {
		...data.petition,
		...getPetitionMetadata(data.petition),
		decisionMakersConnection: {
			totalCount: data.petition.decisionMakersConnection.totalCount,
			nodes: data.petition.decisionMakersConnection.edges.map((edge) => ({
				...edge.node,
				targetPetitionAssociationData: edge.associationData,
			})),
		},
		totalViews,
		totalViewsLocalized,
		promotionStats: getPetitionPromotionStats(data.petition, data.viewer, i18n),
		recommendedDecisionMakers: data.recommendedDecisionMakers?.items || [],
		tagsConnection: data.petition.tagsConnection || { nodes: [] },
		emailDm: { emailSubjectLine, emailBody },
		recentSignersConnection,
	};

	const readyToSharePosts = await getPetitionReadyToSharePosts({ fetch, slugOrId: slug });

	return {
		petition,
		viewer: data.viewer,
		shareRecommendations: {
			shareRecommendationsEnabled: includeShareRecommendations,
		},
		mobileAppDownloadBannerEnabled: data.viewer.experiments.mobileAppDownloadBanner.variation === 'enabled',
		readyToSharePosts,
	};
}
